import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Edit, EventOutlined } from "@mui/icons-material";
import { useDeviceSize } from "../../../hooks";
import { LAPTOP_SIZE_SCREEN, DESKTOP_4K_SIZE_SCREEN } from "../../../constants";
import { ViewDetailsModalProps } from "../../../types/interface";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { LabelCount } from "../../../types/interface/ProjectTrain.interface";

import {
  fetchTasks,
  Task,
  fetchDomains,
  Domain,
} from "../../../store/slice/ProjectTasksSlice";
import { fetchLabelCount } from "../../../store/slice/ProjectTrainSlice";
import VisionStudioSpinner from "../../VisionStudioSpinner";

const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({ project }) => {
  const dispatch: AppDispatch = useDispatch();
  const deviceSize = useDeviceSize();

  const [tasksList, setTasksList] = useState<Task[]>([]);
  const [domainsList, setDomainsList] = useState<Domain[]>([]);
  const [labelDetails, setLabelDetails] = useState<LabelCount[]>([]);

  const { tasks, domains } = useSelector((state: RootState) => state.Tasks);
  const { labelCount } = useSelector((state: RootState) => state.projectTrain);
  const [loader, setLoader] = useState<boolean>(true);

  useEffect(() => {
    dispatch(fetchTasks());
    dispatch(fetchDomains());
    dispatch(fetchLabelCount(project.id));
    setLoader(true);
  }, []);

  useEffect(() => {
    if (tasks.length > 0) {
      setTasksList(tasks);
    }
  }, [tasks]);

  useEffect(() => {
    if (domains.length > 0) {
      setDomainsList(domains);
    }
  }, [domains]);

  useEffect(() => {
    if (labelCount.length > 0) {
      setLabelDetails(labelCount);
    }
    setTimeout(() => {
      setLoader(false);
    }, 1000);
  }, [labelCount, project.id]);

  //#region Lighten the Color
  function lightenColor(hex: string, percent: number) {
    if (!hex) {
      return "#d63e3e47";
    }
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, "");

    // Parse r, g, b values
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    let a = 0.3;

    // Increase each by the percent
    r = Math.min(255, Math.floor(r + ((255 - r) * percent) / 100));
    g = Math.min(255, Math.floor(g + ((255 - g) * percent) / 100));
    b = Math.min(255, Math.floor(b + ((255 - b) * percent) / 100));

    // Convert back to hex
    let newHex =
      "#" +
      r.toString(16).padStart(2, "0") +
      g.toString(16).padStart(2, "0") +
      b.toString(16).padStart(2, "0") +
      Math.round(a * 255)
        .toString(16)
        .padStart(2, "0");

    return newHex;
  }
  //#endregion

  return (
    <Box position="relative" height="auto">
      <Typography
        sx={{
          position: "absolute",
          top: "-20px",
          color: "#000000",
          fontSize: "18px",
        }}
      >
        Details
      </Typography>
      <Card sx={{ borderRadius: 2, marginTop: "20px" }}>
        <CardContent>
          <Box id="projectTypePopup">
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  color: "var(--Color-Base-Black, #313131)",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                {project.name}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", gap: "10px" }}>
                  <TextField
                    fullWidth
                    label="Description"
                    id="description"
                    value={project.description}
                  />
                  {/* <IconButton size="small">
                    <Edit fontSize="small" />
                  </IconButton> */}
                </Box>
                <Box sx={{ display: "flex", gap: "20px" }}>
                  <FormControl
                    sx={{ width: "100%", margin: "10px 0" }}
                    variant="outlined"
                  >
                    <FormHelperText
                      sx={{
                        margin: "0",
                        fontWeight: "500",
                        fontSize: "16px",
                        m: "5px 0",
                        color: "#333333",
                      }}
                    >
                      Task
                      {/* <span style={{ color: "red", fontSize: "10px" }}>*</span> */}
                    </FormHelperText>

                    <Select
                      labelId="dropdown-label"
                      value={project.taskTypeId} // Provide a default value
                      // onChange={(e) =>
                      //   setFormFields({
                      //     ...formFields,
                      //     taskTypeId: e.target.value,
                      //   })
                      // }
                      // sx={{
                      //   width: "100%",
                      //   background: "#F5F6F9",
                      //   border:
                      //     isSubmit && formFields.taskTypeId == "0"
                      //       ? "1px solid red"
                      //       : "0",
                      //   fontSize: "12px",
                      //   borderRadius: "4px",
                      //   "& .MuiSelect-select": {
                      //     padding: "15px",
                      //     fontSize: "15px",
                      //     color: "#9c9c9e",
                      //   },
                      //   "& .MuiOutlinedInput-notchedOutline": {
                      //     border: "none",
                      //   },
                      //   "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      //     border: "none",
                      //   },
                      // }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200, // Set your desired max height
                            overflowY: "auto", // Enable vertical scrolling
                          },
                        },
                      }}
                    >
                      <MenuItem value="0" disabled>
                        Select Task
                      </MenuItem>
                      {tasksList
                        .filter(
                          (task) =>
                            !task.name.toLowerCase().includes("tracking")
                        )
                        .concat(
                          tasksList.filter((task) =>
                            task.name.toLowerCase().includes("tracking")
                          )
                        ).length ? (
                        tasksList
                          .filter(
                            (task) =>
                              !task.name.toLowerCase().includes("tracking")
                          )
                          .concat(
                            tasksList.filter((task) =>
                              task.name.toLowerCase().includes("tracking")
                            )
                          )
                          .map((task) => (
                            <MenuItem key={task.id} value={task.id}>
                              {task.name}
                            </MenuItem>
                          ))
                      ) : (
                        <MenuItem disabled>No tasks available</MenuItem>
                      )}
                    </Select>

                    {/* <Select
                      labelId="dropdown-label"
                      value={project.name} // Provide a default value
                      // onChange={(e) =>
                      //   setFormFields({
                      //     ...formFields,
                      //     taskTypeId: e.target.value,
                      //   })
                      // }
                      sx={{
                        width: "100%",
                        background: "#F5F6F9",
                        // border:
                        //   isSubmit && formFields.taskTypeId == "0"
                        //     ? "1px solid red"
                        //     : "0",
                        fontSize: "12px",
                        borderRadius: "4px",

                        "& .MuiSelect-select": {
                          padding: "10px",
                          fontSize: "15px",
                          color: "#9c9c9e",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                          padding: "10px",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200, // Set your desired max height
                            overflowY: "auto", // Enable vertical scrolling
                          },
                        },
                      }}
                    >
                      <MenuItem value="0" disabled>
                        Select Task
                      </MenuItem>
                      <MenuItem>Task 1</MenuItem>
                      <MenuItem>Task 2</MenuItem>
                      {/* {tasksList.length ? (
                      tasksList.map((task) => (
                        <MenuItem key={task.id} value={task.id}>
                          {task.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No tasks available</MenuItem>
                    )} 
                    </Select> */}
                  </FormControl>

                  <FormControl
                    sx={{ width: "100%", margin: "10px 0" }}
                    variant="outlined"
                  >
                    <FormHelperText
                      sx={{
                        margin: "0",
                        fontWeight: "500",
                        fontSize: "16px",
                        m: "5px 0",
                        color: "#333333",
                      }}
                    >
                      Domain
                      {/* <span style={{ color: "red", fontSize: "10px" }}>*</span> */}
                    </FormHelperText>

                    <Select
                      labelId="dropdown-label"
                      value={project.domainTypeId} // Provide a default value
                      // onChange={(e) =>
                      //   setFormFields({
                      //     ...formFields,
                      //     domainTypeId: e.target.value,
                      //   })
                      // }
                      // sx={{
                      //   width: "100%",
                      //   background: "#F5F6F9",
                      //   border:
                      //     isSubmit && formFields.domainTypeId == "0"
                      //       ? "1px solid red"
                      //       : "0",
                      //   fontSize: "12px",
                      //   borderRadius: "4px",
                      //   "& .MuiSelect-select": {
                      //     padding: "15px",
                      //     fontSize: "15px",
                      //     color: "#9c9c9e",
                      //   },
                      //   "& .MuiOutlinedInput-notchedOutline": {
                      //     border: "none",
                      //   },
                      //   "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      //     border: "none",
                      //   },
                      // }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200, // Set your desired max height
                            overflowY: "auto", // Enable vertical scrolling
                          },
                        },
                      }}
                    >
                      <MenuItem value="0" disabled>
                        Select Domain
                      </MenuItem>
                      {domainsList.length ? (
                        domainsList.map((domian) => (
                          <MenuItem key={domian.id} value={domian.id}>
                            {domian.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No domains available</MenuItem>
                      )}
                    </Select>

                    {/* <Select
                      labelId="dropdown-label"
                      // value={formFields?.domainTypeId || "0"} // Provide a default value
                      // onChange={(e) =>
                      //   setFormFields({
                      //     ...formFields,
                      //     domainTypeId: e.target.value,
                      //   })
                      // }
                      sx={{
                        width: "100%",
                        background: "#F5F6F9",
                        // border:
                        //   isSubmit && formFields.domainTypeId == "0"
                        //     ? "1px solid red"
                        //     : "0",
                        fontSize: "12px",
                        borderRadius: "4px",
                        "& .MuiSelect-select": {
                          padding: "10px",
                          fontSize: "15px",
                          color: "#9c9c9e",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200, // Set your desired max height
                            overflowY: "auto", // Enable vertical scrolling
                          },
                        },
                      }}
                    >
                      <MenuItem value="0" disabled>
                        Select Domain
                      </MenuItem>
                      <MenuItem>Task 1</MenuItem>
                      <MenuItem>Task 2</MenuItem> <MenuItem>Task 1</MenuItem>
                      <MenuItem>Task 2</MenuItem>
                      {/* {domainsList.length ? (
                        domainsList.map((domian) => (
                          <MenuItem key={domian.id} value={domian.id}>
                            {domian.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No domains available</MenuItem>
                      )} 
                    </Select> */}
                  </FormControl>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box
            sx={{ alignItems: "center", gap: "20px" }}
            display={
              deviceSize === DESKTOP_4K_SIZE_SCREEN ||
              deviceSize === LAPTOP_SIZE_SCREEN
                ? "flex"
                : "block"
            }
          >
            <Box sx={{ flex: "0 0 57%" }}>
              <Box mb={2}>
                <Typography
                  sx={{ color: "#0F1D33", fontSize: "16px", margin: "10px 0" }}
                >
                  Status
                </Typography>
                <TableContainer>
                  <Table aria-label="simple table">
                    <TableBody>
                      <TableRow sx={{ border: 0 }}>
                        <TableCell
                        
                          component="td"
                          scope="row"
                          sx={{
                            border: 0,
                            color: "#313131",
                            width: "35%",
                            padding: "5px",
                            fontSize: "12px",
                          }}
                        >
                          Dataset Type:
                        </TableCell>
                        <TableCell
                          component="td"
                          sx={{
                            border: 0,
                            padding: "10px",
                            fontWeight: "600",
                            color: "#313131",
                            fontSize: "12px",
                          }}
                        >
                          Images
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ border: 0 }}>
                        <TableCell
                          component="td"
                          sx={{
                            border: 0,
                            color: "#313131",
                            width: "35%",
                            padding: "5px",
                            fontSize: "12px",
                          }}
                        >
                          Total Images:
                        </TableCell>
                        <TableCell
                          component="td"
                          sx={{
                            border: 0,
                            padding: "10px",
                            fontWeight: "600",
                            color: "#313131",
                            fontSize: "12px",
                          }}
                        >
                          {project.images}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ border: 0, padding: "10px" }}>
                        <TableCell
                          component="td"
                          sx={{
                            border: 0,
                            color: "#313131",
                            width: "35%",
                            padding: "5px",
                            fontSize: "12px",
                          }}
                        >
                          Annotated Images:
                        </TableCell>
                        <TableCell
                          component="td"
                          sx={{
                            border: 0,
                            padding: "5px",
                            fontWeight: "600",
                            color: "#313131",
                            fontSize: "12px",
                          }}
                        >
                          {project.annotations}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box mb={2}>
                <Box id="labelProjectPopup">
                  <Typography variant="subtitle2" sx={{ margin: "10px 0" }}>
                    Labels
                  </Typography>
                  {loader ? (
                    <VisionStudioSpinner />
                  ) : (
                    <Box
                      sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                    >
                      {labelDetails.length > 0 ? (
                        labelDetails.map((label) => {
                          return (
                            <Typography
                              variant="h6"
                              sx={{
                                color: "black",
                                background: lightenColor(label.labelColor, 10),
                                fontSize: "11px",
                                alignItems: "center",
                                display: "flex",
                                padding: "4px 10px",
                              }}
                            >
                              {label.labelName}
                              <span
                                style={{
                                  color: "white",
                                  background: "#1D97F3",
                                  fontSize: "11px",
                                  alignItems: "center",
                                  display: "flex",
                                  padding: "1px 4px",
                                  marginLeft: "3px",
                                }}
                              >
                                {label.count}
                              </span>
                            </Typography>
                          );
                        })
                      ) : (
                        <p style={{ color: "red" }}>No Labels</p>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                mb={2}
                position="relative"
              >
                {/* <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    marginLeft: "20px",
                    "&:before": {
                      left: "-3px",
                      top: "8px",
                      content: '""',
                      position: "absolute",
                      height: "14px",
                      width: "14px",
                      background: "red",
                      borderRadius: "50%",
                      marginRight: "20px",
                    },
                  }}
                >
                  Been assigned
                </Typography> */}
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginLeft: "auto" }}
                >
                  <IconButton size="small">
                    <EventOutlined fontSize="small" />
                  </IconButton>
                  {new Intl.DateTimeFormat("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }).format(new Date(project.createdDate))}
                </Typography>
              </Box>

              {/* <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap="20px"
              >
                <Box display="flex" alignItems="center" flexWrap="wrap">
                  <Avatar
                    alt="Person 1"
                    src="/static/images/avatar/1.jpg"
                    sx={{ width: 30, height: 30, marginRight: "-6px" }}
                  />
                  <Avatar
                    alt="Person 2"
                    src="/static/images/avatar/2.jpg"
                    sx={{ width: 30, height: 30, marginRight: "-6px" }}
                  />
                  <Avatar
                    alt="Person 3"
                    src="/static/images/avatar/3.jpg"
                    sx={{ width: 30, height: 30, marginRight: "-6px" }}
                  />
                </Box>
                <Box sx={{ flex: "0 0 39%" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ marginLeft: "auto", height: "40px" }}
                  >
                    Add Members
                  </Button>
                </Box>
              </Box> */}
            </Box>
            {/* <Box
              sx={{ justifyContent: "center", width: "100%", flex: "0 0 40%" }}
              marginTop={
                deviceSize === DESKTOP_4K_SIZE_SCREEN ||
                deviceSize === LAPTOP_SIZE_SCREEN
                  ? "0"
                  : "20px"
              }
            >
              <img
                src={`${process.env.REACT_APP_SERVER_BASEURL}${project.imageUrl}`}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box> */}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ViewDetailsModal;
